import { FC } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { ReviewSentimentAnalysesSection } from 'src/charts/ReviewSentimentAnalysesSection'
import { ScopedSummarySection } from 'src/charts/SummarySection'
import { BackNavigationButton } from 'src/components/BackNavigationButton'
import { REVIEW_ANALYSIS_CAMPAIGN_ROUTE } from 'src/constants/routes'
import { PLATFORM_ORDER } from 'src/pages/ReviewAnalysis/constants'
import { useGetReviewAnalysisFacilityOverviewQuery } from 'src/reactQuery/useGetReviewAnalysisFacilityOverviewQuery'
import {
  useReviewAnalysisDateRange,
  useReviewAnalysisLinkQuery,
} from 'src/hooks/useReviewAnalysisDateRange'
import { createLink } from 'src/utils/createLink'
import { FacilityReviewAnalysis } from './FacilityReviewAnalysis'
import { TopReviewTopics } from './TopReviewTopics'
import { ReviewTopicsAnalysis } from '../BrandLevelWidgets/ReviewTopicsAnalysis'
import { ReviewSmartInsights } from '../ReviewSmartInsights'
import classes from '../BrandLevelWidgets/brandLevelWidgets.module.scss'

export const FacilityLevelWidgets: FC = () => {
  const { campaignId = '', facilityId = '' } = useParams<{
    campaignId: string
    facilityId: string
  }>()
  // Same ?platform the FacilityReviewAnalysis tabs write. Reading it here rather
  // than lifting state keeps the URL the single source of truth, so the stats
  // and the insights panel cannot drift apart. Absent means All Platforms, and
  // the endpoint treats an absent platformId as "do not filter".
  const [searchParams] = useSearchParams()
  const activePlatform = searchParams.get('platform') || undefined
  // The reviews list takes only a known platform, as the platform tabs do; a
  // location card's View Details adds ?venue to scope it to that location.
  const reviewsPlatform = PLATFORM_ORDER.find((p) => p === activePlatform)
  const venueName = searchParams.get('venue') || undefined

  // The overview query is shared with FacilityReviewAnalysis (react-query dedupes
  // it), so reading the facility name here is essentially free.
  const dateRange = useReviewAnalysisDateRange()
  const linkQuery = useReviewAnalysisLinkQuery()
  const { data: facilityOverview } = useGetReviewAnalysisFacilityOverviewQuery(
    campaignId,
    facilityId,
    dateRange
  )

  return (
    <div className={classes.wrapper}>
      <div className={classes.backNav}>
        <BackNavigationButton
          className={classes.backNavButton}
          title={facilityOverview?.facility.name ?? ''}
          url={createLink(REVIEW_ANALYSIS_CAMPAIGN_ROUTE, {
            params: { campaignId },
            queryParams: linkQuery,
          })}
        />
      </div>
      <ReviewSmartInsights
        facilityId={facilityId}
        platformId={activePlatform}
      />
      <FacilityReviewAnalysis />
      {/* The Topics & Subtopics table lives on the facility page, scoped to it
          and to the platform tab, the way a Social Listening campaign page
          carries the table while the campaigns overview carries the card. */}
      <div className={classes.topicsAnalysis}>
        <ReviewTopicsAnalysis
          campaignId={campaignId}
          facilityId={facilityId}
          platform={activePlatform}
          venueName={venueName}
        />
      </div>
      <TopReviewTopics level="facility" variant="positive" />
      <TopReviewTopics level="facility" variant="negative" />
      <div className={classes.summarySection}>
        <ScopedSummarySection
          scope={{ facilityId, id: campaignId, kind: 'reviewAnalysis' }}
        />
      </div>
      <div className={classes.sentimentSection}>
        <ReviewSentimentAnalysesSection
          campaignId={campaignId}
          facilityId={facilityId}
          platform={reviewsPlatform}
          venueName={venueName}
        />
      </div>
    </div>
  )
}
